import type { RoleAccessRecordSnapshotType } from "./access-store";

/**
 * Права для экранов доступа: модуль → раздел → действия. Разбирается из меток
 * Functionality.Labels («Модуль. Раздел. Действие.»), порядок — как в словаре на бэке.
 */
export interface AccessItem {
    code: string;
    /** Действие из метки как есть: «Чтение», «Запись (Все)», «Важное». Пусто — у раздела одно право */
    action: string;
}

export interface AccessSection {
    name: string;
    items: AccessItem[];
}

export interface AccessModuleData {
    name: string;
    sections: AccessSection[];
    codes: string[];
}

export const ACTION_READ = "Чтение";
export const ACTION_EDIT = "Запись";

/** Причина у прав полного доступа (GodMode) — тот же текст отдаёт бэк, Functionality.GOD_MODE_REASON */
export const GOD_MODE_REASON = "Полный доступ";

/**
 * Пояснения к правам, чьё название без него непонятно: подсказка при наведении на раздел и галочку
 * на странице прав доступа, в матрице ролей и в карточке сотрудника
 */
export const ACCESS_HINTS: Record<string, string> = {
    ORDERS_ALL_EDIT:
        "Править договоры, в которых сотрудник не указан «ГИП / Рук. проекта».\n" +
        "Без этого права правка вкладок договора (при праве «Запись» на них) доступна только в договорах, " +
        "где сотрудник — ГИП. Создавать новые договоры можно и без него.\n" +
        "Даётся всем сотрудникам по умолчанию: чтобы ограничить, поставьте сотруднику личный запрет.",
};

/** Пояснения ко всем правам раздела одной строкой — подсказка к названию раздела */
export const sectionHint = (items: { code: string }[]) =>
    items
        .map((i) => ACCESS_HINTS[i.code])
        .filter((h) => !!h)
        .join("\n\n");

/** Подпись галочки с пояснением права, если оно есть */
export const withHint = (label: string, code: string) => (ACCESS_HINTS[code] ? `${label}\n\n${ACCESS_HINTS[code]}` : label);

const parseLabel = (label: string) => {
    const parts = label
        .split(".")
        .map((p) => p.trim())
        .filter((p) => !!p);

    return {
        module: parts[0] ?? "",
        section: parts[1] ?? parts[0] ?? "",
        action: parts.slice(2).join(" "),
    };
};

export function buildAccessModules(authorization: [string, string][]): AccessModuleData[] {
    const modules: AccessModuleData[] = [];

    for (const [code, label] of authorization) {
        const { module, section, action } = parseLabel(label);

        let mod = modules.find((m) => m.name === module);
        if (!mod) {
            mod = { name: module, sections: [], codes: [] };
            modules.push(mod);
        }

        let sec = mod.sections.find((s) => s.name === section);
        if (!sec) {
            sec = { name: section, items: [] };
            mod.sections.push(sec);
        }

        sec.items.push({ code, action });
        mod.codes.push(code);
    }

    return modules;
}

/** Колонки таблицы: «Чтение / доступ», «Запись» и остальное флажками */
export function splitSection(items: AccessItem[]) {
    const read = items.find((i) => i.action === ACTION_READ || !i.action);
    const edit = items.find((i) => i.action === ACTION_EDIT);
    const rest = items.filter((i) => i !== read && i !== edit);

    return { read, edit, rest };
}

/**
 * Состояние права в карточке:
 * on — по должности, mgr — как руководителю подразделения,
 * plus / minus — личное разрешение / запрет, off — нет права
 */
export type AccessState = "on" | "mgr" | "plus" | "minus" | "off";

export function accessState(record: RoleAccessRecordSnapshotType | undefined, personal: boolean): AccessState {
    if (!record) {
        return "off";
    }

    if (personal && record.overwritten) {
        return record.value ? "plus" : "minus";
    }

    if (!record.value) {
        return "off";
    }

    return personal && record.reason && record.reason !== GOD_MODE_REASON ? "mgr" : "on";
}

export const isEnabled = (state: AccessState) => state !== "off" && state !== "minus";

export const isGodMode = (access: RoleAccessRecordSnapshotType[]) =>
    access.length > 0 && access.every((a) => a.reason === GOD_MODE_REASON);

export function matches(query: string, ...texts: string[]) {
    const q = query.trim().toLowerCase();
    return !q || texts.join(" ").toLowerCase().includes(q);
}
